import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ColorLens from '@mui/icons-material/ColorLens';
import ThemeEditor from './ThemeEditor';
import SavedThemes from './SavedThemes';
import { ThemeObject } from './ThemeEditor';
import fetchUserThemes from '../api/fetchUserThemes';
import SSOAccess from './SSO/ssoAccess';
import SecurityIcon from '@mui/icons-material/Security';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux';
import { setDefaultThemeId } from '../redux/TabTile/TabActions';
import Header from './Header';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const userRole = sessionStorage.getItem('access');

  const [activeSection, setActiveSection] = useState<'theme' | 'sso'>('theme');
  // const [showThemesSection, setShowThemesSection] = useState(true);
  const [showThemeEditor, setShowThemeEditor] = useState(false);
  const [userThemes, setUserThemes] = useState<ThemeObject[]>([]);
  const [savedThemes, setSavedThemes] = useState<string[]>([]);
  const [editingTheme, setEditingTheme] = useState<ThemeObject | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  const dispatch = useDispatch();

  const selectedTabId = useSelector((state: RootState) => state.tabTileProps.selectedTabId);

  const handleClose = () => {
    navigate('/');
  };

  const handleThemeClick = () => {
    navigate('/settings');
    setActiveSection('theme');
    setShowThemeEditor(false);
    setEditingTheme(null);
  };

  const handleSSOTabClick = () => {
    navigate('/settings/sso-configuration');
  };

  const handleCreateNewTheme = () => {
    setEditingTheme(null);
    setShowThemeEditor(true);
  };

  const handleEditTheme = (theme: ThemeObject) => {
    setEditingTheme(theme);
    setShowThemeEditor(true);
  };

  const handleBackToThemes = () => {
    setShowThemeEditor(false);
    setEditingTheme(null);
  };

  const addUserTheme = (newTheme: ThemeObject) => {
    setUserThemes((prev) => [...prev, newTheme]);
    setSavedThemes((prev) => [...prev, newTheme.themeName.toLowerCase()]);
  };

  const handleDeleteTheme = (themeId: string) => {
    const deletedTheme = userThemes.find((t) => t.themeId === themeId);
    if (!deletedTheme) return;

    setUserThemes((prevThemes) => prevThemes.filter((theme) => theme.themeId !== themeId));
    setSavedThemes((prev) => prev.filter((name) => name !== deletedTheme.themeName.toLowerCase()));
  };

  const updateUserTheme = (updatedTheme: ThemeObject) => {
    setUserThemes((prevThemes) =>
      prevThemes.map((theme) => (theme.themeId === updatedTheme.themeId ? updatedTheme : theme)),
    );
  };

  const refreshThemes = async () => {
    const { themes, defaultThemeId } = await fetchUserThemes();

    setUserThemes(themes);
    setSavedThemes(themes.map((t) => t.themeName.toLowerCase()));

    if (selectedTabId) dispatch(setDefaultThemeId(selectedTabId, defaultThemeId));
  };

  const location = useLocation();

  useEffect(() => {
    if (location.pathname.includes('/settings/sso-configuration')) {
      if (userRole !== 'account_admin' && userRole !== 'admin') {
        navigate('/settings', { replace: true });
        return;
      }
      setActiveSection('sso');
    } else {
      setActiveSection('theme');
    }
  }, [location.pathname]);

  useEffect(() => {
    const loadThemes = async () => {
      const { themes, defaultThemeId } = await fetchUserThemes();

      setUserThemes(themes);
      setSavedThemes(themes.map((t) => t.themeName.toLowerCase()));

      dispatch(setDefaultThemeId(selectedTabId, defaultThemeId));
    };

    loadThemes();
  }, [selectedTabId]);

  return (
    <>
      <Helmet prioritizeSeoTags defaultTitle="Settings" defer>
        <title>Settings | Silzila</title>
      </Helmet>
      <div
        style={{
          padding: '0.5rem',
        }}
      >
        <Header />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          height: '100vh',
          width: '100%',
          backgroundColor: '#ffffff',
          overflow: 'auto',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: '180px',
            backgroundColor: '#ffffff',
            borderRight: '1px solid #ddd',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#484848',
              letterSpacing: '0.5px',
              marginBottom: '10px',
            }}
          >
            SETTINGS
          </Typography>

          <Box
            onClick={handleThemeClick}
            sx={{
              alignSelf: 'stretch',
              display: 'flex',
              gridTemplateColumns: '40px 1fr',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1,
              my: 0.5,
              cursor: 'pointer',
              outline: activeSection === 'theme' ? '1px solid #818181' : '1px solid transparent',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ColorLens sx={{ fontSize: 20 }} />
            </Box>

            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                minWidth: 0,
                color: '#484848',
              }}
            >
              Theme
            </Typography>
          </Box>

          {/* SSO Config Button */}
          {(userRole === 'account_admin' || userRole === 'admin') && (
            <Box
              onClick={handleSSOTabClick}
              sx={{
                alignSelf: 'stretch',
                display: 'flex',
                gridTemplateColumns: '40px 1fr',
                alignItems: 'center',
                gap: 1.8,
                px: 2,
                py: 1,
                my: 0.5,
                cursor: 'pointer',
                outline: activeSection === 'sso' ? '1px solid #818181' : '1px solid transparent',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <SecurityIcon sx={{ fontSize: 18 }} />
              </Box>

              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  minWidth: 0,
                  color: '#484848',
                }}
              >
                SSO
              </Typography>
            </Box>
          )}
        </div>

        <div
          style={{
            flex: 1,
            padding: '20px',
            minWidth: 0,
            overflowX: 'auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              flexWrap: 'wrap',
              marginBottom: '20px',
            }}
          >
            <span
              onClick={handleClose}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              style={{
                cursor: 'pointer',
                color: isHovered ? '#2bb9bb' : '#484848',
                fontWeight: 500,
                fontSize: '12px',
                transition: 'color 0.2s',
              }}
            >
              Home
            </span>
            <span style={{ margin: '0 10px', color: '#484848' }}>{'>'}</span>
            <span style={{ margin: '0 10px', color: '#484848' }}>{'Settings'}</span>
            <span style={{ margin: '0 10px', color: '#484848' }}>{'>'}</span>
            <span>
              {activeSection === 'sso'
                ? 'SSO Configuration'
                : showThemeEditor
                  ? 'Theme Builder'
                  : 'Saved Themes'}
            </span>
          </div>

          {activeSection === 'theme' && (
            <>
              {!showThemeEditor ? (
                <SavedThemes
                  onCreateNew={handleCreateNewTheme}
                  onDeleteTheme={handleDeleteTheme}
                  onEditTheme={handleEditTheme}
                  refreshThemes={refreshThemes}
                />
              ) : (
                <ThemeEditor
                  savedThemes={savedThemes}
                  setSavedThemes={setSavedThemes}
                  onBack={handleBackToThemes}
                  addUserTheme={addUserTheme}
                  editingTheme={editingTheme}
                  updateUserTheme={updateUserTheme}
                  refreshThemes={refreshThemes}
                />
              )}
            </>
          )}
          {activeSection === 'sso' && (
            <Box sx={{ maxWidth: '800px', margin: '0 auto' }}>
              <SSOAccess />
            </Box>
          )}
        </div>
      </div>
    </>
  );
};

export default SettingsPage;
